import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { addMonths, format, subMonths } from 'date-fns';
import { useState } from 'react';
import { SmallSecondaryButton } from '../../components/buttons';
import { EmptyState } from '../../components/empty-state';
import { Loading } from '../../components/loading';
import { Cell, Row, Table } from '../../components/tables';
import { parseVendorState, sortVendorStats } from '../../data/admin';
import { longMonthName } from '../../helpers/months';
import { usePricingStats, useTriggerAllNysla, useTriggerPricing } from '../../hooks/pricing';

export function PricingTab() {
  const { data: pricingStats, isLoading } = usePricingStats();
  const triggerPricing = useTriggerPricing();
  const triggerAllNysla = useTriggerAllNysla();

  // Default to next month
  const currentMonthDate = new Date();
  const previousMonthDate = subMonths(currentMonthDate, 1);
  const nextMonthDate = addMonths(currentMonthDate, 1);

  const previousMonthName = `${longMonthName(previousMonthDate)} ${previousMonthDate.getFullYear()}`;
  const currentMonthName = `${longMonthName(currentMonthDate)} ${currentMonthDate.getFullYear()}`;
  const nextMonthName = `${longMonthName(nextMonthDate)} ${nextMonthDate.getFullYear()}`;

  const defaultYear = nextMonthDate.getFullYear();
  const defaultMonth = nextMonthDate.getMonth() + 1; // Convert to 1-based month
  const defaultMonthValue = `${defaultYear}-${defaultMonth.toString().padStart(2, '0')}`;
  const [vendorMonths, setVendorMonths] = useState<Record<string, string>>({});
  const [vendorSources, setVendorSources] = useState<Record<string, string>>({});

  const getVendorMonth = (vendorName: string) => {
    return vendorMonths[vendorName] || defaultMonthValue;
  };

  const setVendorMonth = (vendorName: string, monthValue: string) => {
    setVendorMonths((prev) => ({
      ...prev,
      [vendorName]: monthValue,
    }));
  };

  const getVendorSource = (vendorName: string, validSources: string[]) =>
    vendorSources[vendorName] ?? validSources[0] ?? '';

  const setVendorSource = (vendorName: string, source: string) => {
    setVendorSources((prev) => ({ ...prev, [vendorName]: source }));
  };

  const sourceLabel = (source: string): string => {
    switch (source) {
      case 'nysla':
        return 'NYSLA';
      case 'drive':
        return 'Google Drive';
      default:
        return source;
    }
  };

  const nyslaMonthLabel = (): string => {
    const now = new Date();
    const target = now.getDate() >= 15 ? addMonths(now, 1) : now;

    return format(target, 'MMM yyyy');
  };

  const handleTriggerUpdate = (vendorName: string, validSources: string[]) => {
    const source = getVendorSource(vendorName, validSources);
    if (source === 'nysla') {
      triggerPricing.mutate({ vendor_name: vendorName, source });
    } else {
      const monthValue = getVendorMonth(vendorName);
      const [year, month] = monthValue.split('-').map(Number);
      triggerPricing.mutate({ vendor_name: vendorName, source, year, month });
    }
  };

  const formatCount = (count: number) => (count === 0 ? '-' : count.toLocaleString());

  // Only show loading if we don't have cached data
  if (!pricingStats && isLoading) {
    return <Loading />;
  }

  const sortedVendors = pricingStats ? sortVendorStats(pricingStats) : [];

  // Group vendors by state
  const vendorsByState = sortedVendors.reduce(
    (acc, vendor) => {
      const { state } = parseVendorState(vendor.vendor_name);
      const stateKey = state || 'Other';
      if (!acc[stateKey]) {
        acc[stateKey] = [];
      }

      acc[stateKey].push(vendor);

      return acc;
    },
    {} as Record<string, typeof sortedVendors>
  );

  // Sort states alphabetically
  const sortedStates = Object.keys(vendorsByState).sort();

  return (
    <div className="container mx-auto">
      <div className="mb-6 mt-4 space-y-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          <h3 className="mb-2 font-semibold">Google Drive Import</h3>
          <p className="mb-2">
            The selected month corresponds to the <strong>file name</strong>, which is always the{' '}
            <strong>newest month</strong> of data in the file.
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Single-month files</strong> (ARMA_OK, CPTL_OK, LDF_OK, OPICI_NY, SGWS_NY,
              SGWS_AR): The file contains only one month of pricing.
              <br />
              <span className="text-blue-600">
                Example: Selecting "January 2025" imports <code>OK-CPTL-2025-1.xlsx</code> → January
                pricing only.
              </span>
            </li>
            <li>
              <strong>Two-month files</strong> (RNDC_OK, SGWS_OK, EMPR_NY): The file contains both
              current and next month pricing.
              <br />
              <span className="text-blue-600">
                Example: Selecting "January 2025" imports <code>OK-SGWS-2025-1.xlsx</code> →
                December 2024 + January 2025 pricing.
              </span>
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <h3 className="mb-2 font-semibold">NYSLA Import</h3>
          <p className="mb-2">
            NYSLA pricing is fetched directly from the{' '}
            <strong>NY State Liquor Authority API</strong> — no file or month selection needed. The
            API automatically serves the active posting period:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>1st–14th of the month:</strong> current month's filed schedules
            </li>
            <li>
              <strong>15th–end of month:</strong> next month's filed schedules
            </li>
          </ul>
          <p className="mt-2">
            Each vendor is fetched for both <strong>Liquor</strong> and <strong>Wine</strong>{' '}
            postings. Imports are rate-limited and may take several minutes per vendor.
          </p>
        </div>
      </div>

      {sortedStates.length > 0 ? (
        <div className="space-y-8">
          {sortedStates.map((state) => {
            const hasNysla = vendorsByState[state].some((v) => v.valid_sources.includes('nysla'));

            return (
              <div key={state}>
                <div className="mb-4 flex items-center gap-3">
                  <h2 className="text-xl font-semibold">{state}</h2>
                  {hasNysla && (
                    <SmallSecondaryButton
                      onClick={() => triggerAllNysla.mutate()}
                      disabled={triggerAllNysla.isPending}
                    >
                      {triggerAllNysla.isPending ? 'Updating...' : 'Update all NYSLA'}
                    </SmallSecondaryButton>
                  )}
                </div>
                <Table
                  headings={[
                    'Vendor',
                    previousMonthName,
                    currentMonthName,
                    nextMonthName,
                    <div key="actions-heading" className="text-right">
                      Actions
                    </div>,
                  ]}
                >
                  {vendorsByState[state].map((vendor) => {
                    const { vendor: vendorName } = parseVendorState(vendor.vendor_name);

                    return (
                      <Row key={vendor.vendor_name}>
                        <Cell>{vendorName}</Cell>
                        <Cell>
                          <span
                            className={vendor.previous_month_count === 0 ? 'text-gray-400' : ''}
                          >
                            {formatCount(vendor.previous_month_count)}
                          </span>
                        </Cell>
                        <Cell>
                          <span className={vendor.current_month_count === 0 ? 'text-gray-400' : ''}>
                            {formatCount(vendor.current_month_count)}
                          </span>
                        </Cell>
                        <Cell>
                          <span className={vendor.next_month_count === 0 ? 'text-gray-400' : ''}>
                            {formatCount(vendor.next_month_count)}
                          </span>
                        </Cell>
                        <Cell>
                          <div className="flex items-center justify-end gap-2">
                            <select
                              value={getVendorSource(vendor.vendor_name, vendor.valid_sources)}
                              onChange={(e) => setVendorSource(vendor.vendor_name, e.target.value)}
                              disabled={vendor.valid_sources.length <= 1}
                              className="w-32 text-xs rounded border-gray-300 disabled:opacity-60 disabled:cursor-default"
                            >
                              {vendor.valid_sources.map((src) => (
                                <option key={src} value={src}>
                                  {sourceLabel(src)}
                                </option>
                              ))}
                            </select>
                            {getVendorSource(vendor.vendor_name, vendor.valid_sources) ===
                            'nysla' ? (
                              <span className="w-32 text-xs text-gray-500 text-center">
                                {nyslaMonthLabel()}
                              </span>
                            ) : (
                              <input
                                type="month"
                                value={getVendorMonth(vendor.vendor_name)}
                                onChange={(e) => setVendorMonth(vendor.vendor_name, e.target.value)}
                                className="w-32 text-xs rounded border-gray-300"
                              />
                            )}
                            <SmallSecondaryButton
                              onClick={() =>
                                handleTriggerUpdate(vendor.vendor_name, vendor.valid_sources)
                              }
                              disabled={triggerPricing.isPending}
                            >
                              {triggerPricing.isPending ? 'Importing...' : 'Import Pricing'}
                            </SmallSecondaryButton>
                          </div>
                        </Cell>
                      </Row>
                    );
                  })}
                </Table>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<CurrencyDollarIcon className="size-8" />}
          title="No pricing data available."
        />
      )}
    </div>
  );
}
